import {
    Component,
    EventEmitter,
    forwardRef,
    Injector,
    Input,
    OnDestroy,
    Output,
} from '@angular/core';
import {
    AbstractControl,
    ControlValueAccessor,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    ValidationErrors,
    Validator,
} from '@angular/forms';
import { CmpsDialog, CmpsDialogRef } from '@cmps/ng-uikit';
import { filter, tap } from 'rxjs/operators';

import { FocusSelectionParams } from '../../coaching-base/config';
import { FocusDto } from '../../coaching-base/general';
import { CommitmentGoalMetric } from '../../coaching-base/general/commitment-goal-metric.model';
import {
    TakeUntilDestroy,
    takeUntilDestroy,
} from '../../common/take-until-destroy.decorator';
import { isActiveFocus } from '../is-active-focus.function';
import { TagsDialogResult } from '../tags-dialog-result.model';

import { TagsComponent } from './tags.component';
import { TagsDialogData, TagSetMode } from './tags.model';

@Component({
    selector: 'cmps-tag-set-trigger',
    template: '',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => TagSetTriggerComponent),
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => TagSetTriggerComponent),
            multi: true,
        },
    ],
})
@TakeUntilDestroy()
export class TagSetTriggerComponent
    implements OnDestroy, ControlValueAccessor, Validator
{
    @Input() storePathPrefix: string;
    @Input() mode: TagSetMode;
    @Input() focusSelectionParams: FocusSelectionParams;
    @Input() commitmentId: number;
    @Input() performerId: number;
    @Input() selected: FocusDto;
    @Input() pinnedMetrics: number[];
    @Input() activeMetricId: number;
    @Output() save: EventEmitter<FocusDto>;
    private _onChange: (_: FocusDto) => void;
    private _onTouched: () => void;
    private _onValidatorChange: () => void;
    private _dialogRef: CmpsDialogRef<TagsComponent, TagsDialogResult>;
    private _lastDialogSelected: FocusDto;
    private _lastDialogActiveCommitmentFocuses: CommitmentGoalMetric[];
    private _validationErrors: ValidationErrors;

    constructor(
        private readonly _injector: Injector,
        private readonly _dialog: CmpsDialog,
    ) {
        this.save = new EventEmitter();
    }

    ngOnDestroy(): void {
        this.save.complete();
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._dialogRef && this._dialogRef.close();
    }

    registerOnChange(fn: any): void {
        this._onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this._onTouched = fn;
    }

    writeValue(obj: FocusDto): void {
        this.selected = obj;
    }

    registerOnValidatorChange(fn: () => void): void {
        this._onValidatorChange = fn;
    }

    validate(control: AbstractControl): ValidationErrors | null {
        this._validate(
            this._lastDialogSelected || this.selected,
            this._lastDialogActiveCommitmentFocuses,
        );
        return this._validationErrors;
    }

    open(): void {
        const data: TagsDialogData = {
            storePathPrefix: this.storePathPrefix,
            mode: this.mode == null ? TagSetMode.DEFAULT : this.mode,
            focusSelectionParams: this.focusSelectionParams,
            commitmentId: this.commitmentId,
            performerId: this.performerId,
            selected: this.selected,
            pinnedMetrics: this.pinnedMetrics,
            activeMetricId: this.activeMetricId,
        };

        this._dialogRef = this._dialog.open(TagsComponent, {
            injector: this._injector,
            data,
        });
        this._dialogRef.result
            .pipe(
                filter((ret) => !!ret),
                tap((ret) => {
                    this._lastDialogSelected = ret.selected;
                    this._lastDialogActiveCommitmentFocuses =
                        ret.activeCommitmentFocuses;
                    this._validate(ret.selected, ret.activeCommitmentFocuses);
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    this._onChange && this._onChange(ret.selected);
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    this.save && this.save.emit(ret.selected);
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    private _validate(
        selected: FocusDto,
        activeCommitmentFocuses: CommitmentGoalMetric[],
    ): void {
        const obj: ValidationErrors = {};
        if (!selected || selected.behaviourId == null) {
            obj.required = null;
        }
        if (
            isActiveFocus(selected, activeCommitmentFocuses, this.commitmentId)
        ) {
            obj.activeFocus = true;
        }
        this._validationErrors = Object.keys(obj).length !== 0 ? obj : null;
    }
}
